import React from "react";
import t from "../../../i18n";
import ModalForm from "@ui/Modal/Form";
import type { Analysis } from "../../../api/Analysis";
import type { Project } from "../../../api/useProjects";
import Select from "@ui/Select";
import useInput from "@ui/lib/hooks/useInput";
import RadioButton from "@ui/RadioButton";
import useActiveAnalysesStream from "../../../api/useActiveAnalysesStream";
import { color } from "@ui";
import { colors, spacings } from "@ui/Theme";
import { analysisTitleLong } from "./analysisTitle";
import { usePost } from "../../../api/useCall";
import download from "downloadjs";
import Text from "@ui/Text";
import { generateExportFileName } from "diagnostics-lib/string";
import { css } from "@linaria/core";

// Enum + List
enum FORMAT {
  CSV_LITHIUM = "CSV_LITHIUM",
  CSV_HELIUM = "CSV_HELIUM",
}

enum RADIO {
  SELECTED_ANALYSES = "SELECTED_ANALYSES",
  ALL_ANALYSES = "ALL_ANALYSES",
}

const options = [
  {
    value: FORMAT.CSV_HELIUM,
    label: t("project.modal.analysis_export.option.format.csv_helium"),
  },
  {
    value: FORMAT.CSV_LITHIUM,
    label: t("project.modal.analysis_export.option.format.csv_lithium"),
  },
];

// Interface
interface P {
  project: Project;
  selectedAnalyses: Analysis[];
  closeModal: () => void;
}

// Component
const AnalysisExportModalForm = ({ project, selectedAnalyses, closeModal }: P) => {
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const analysisExport = usePost("/projects/:projectId(uuid)/analyses/export");

  const { value: formatValue, onChange: setFormatValue } = useInput(FORMAT.CSV_LITHIUM);
  const [radioValue, setRadioValue] = React.useState(RADIO.SELECTED_ANALYSES);

  const handleSubmit = React.useCallback(async () => {
    const format =
      formatValue === FORMAT.CSV_LITHIUM
        ? "lithium"
        : formatValue === FORMAT.CSV_HELIUM
          ? "helium"
          : undefined;

    const analysisDates =
      radioValue === RADIO.SELECTED_ANALYSES
        ? selectedAnalyses.map(s => new Date(s.date))
        : activeAnalyses.map(s => new Date(s.date));

    try {
      const reponse = await analysisExport({
        routeParams: { projectId: project.id },
        query: { format },
        body: {
          analysisDates,
        },
      });

      const fileName = generateExportFileName({
        filename: "analyses",
        projectName: project.name,
        timezone: project.timezone,
        extension: "csv",
      });
      const blob = new Blob([reponse.body as unknown as Blob], { type: "text/csv;charset=utf-8" });
      download(blob, fileName, "text/csv");
    } catch (error) {
      return false;
    }

    return true;
  }, [
    activeAnalyses,
    analysisExport,
    formatValue,
    project.id,
    project.name,
    project.timezone,
    radioValue,
    selectedAnalyses,
  ]);

  const isSubmitted =
    (radioValue === RADIO.SELECTED_ANALYSES && selectedAnalyses.length > 0) ||
    (radioValue === RADIO.ALL_ANALYSES && activeAnalyses.length > 0);

  return (
    <ModalForm
      className={modalForm}
      onRequestClose={closeModal}
      onSubmit={isSubmitted ? handleSubmit : false}
      okButtonName={t("project.charts.modal.form.analysis_export.button.name")}
      cancellable
    >
      <Select
        label={t("projects.modal.analysis_export.title.format")}
        prompt=""
        required
        value={formatValue}
        onChange={setFormatValue}
        options={options}
        clearable={false}
      />

      <Text className={timezone}>
        {t("project.charts.modal.form.analysis_export.timezone", project.timezone)}
      </Text>

      <div className={field}>
        <Text className={exportDataTitle} type="body1SemiBold">
          {t("project.modal.analysis_export.title.data_to_export")}
          <sup className={sup}>*</sup>
        </Text>
        <RadioButton
          className={radioButton}
          checked={radioValue === RADIO.SELECTED_ANALYSES}
          onChange={() => setRadioValue(RADIO.SELECTED_ANALYSES)}
        >
          {t(
            "project.modal.analysis_export.radio_button.title.selected_analyses",
            selectedAnalyses.length,
          )}
        </RadioButton>
        <RadioButton
          className={radioButton}
          checked={radioValue === RADIO.ALL_ANALYSES}
          onChange={() => setRadioValue(RADIO.ALL_ANALYSES)}
        >
          {t(
            "project.modal.analysis_export.radio_button.title.all_analyses",
            activeAnalyses.length,
          )}
        </RadioButton>
      </div>

      {radioValue === RADIO.SELECTED_ANALYSES && (
        <div className={field}>
          <Text className={analysisListTitle}>
            {t("project.modal.analysis_export.title.exported_analyses")}
          </Text>
          <div className={containerSelectedAnalyses}>
            <ul>
              {selectedAnalyses.map(analysis => (
                <li key={analysis.id}>
                  <li className={SublistItem}>
                    <div className={puce} />
                    <Text type="body2">
                      {analysisTitleLong(analysis, project ? project.timezone : null)}
                    </Text>
                  </li>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </ModalForm>
  );
};

export default React.memo(AnalysisExportModalForm);

//#region CSS
const modalForm = css`
  row-gap: 0;
`;

const timezone = css`
  margin: ${spacings.m} 0;
`;

const field = css`
  display: flex;
  flex-direction: column;
  row-gap: ${spacings.xxs};
`;

const exportDataTitle = css`
  color: ${color.carbon800};
`;
const analysisListTitle = css`
  color: ${color.carbon800};
  margin-top: ${spacings.xs};
`;

const puce = css`
  margin: 0 5px;
  height: 3px;
  width: 3px;
  border-radius: 50%;
  background-color: ${color.carbon600};
`;

const sup = css`
  color: ${colors.darkNitrogen600};
  vertical-align: top;
`;

const SublistItem = css`
  margin-left: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
  color: ${color.carbon800};
`;

const radioButton = css`
  margin-left: ${spacings.xxs};
`;

const containerSelectedAnalyses = css`
  max-height: 110px;
  overflow-y: auto;
`;
//#endregion CSS
